/**
 * UGC campaign category / sub-category options.
 * Mirrors backend CategorySchema enums (category + sub_category).
 */

export const NICHE_MAIN_ORDER = [
    'Fashion',
    'Beauty & Skincare',
    'Fitness & Health',
    'Lifestyle',
    'Travel & Adventure',
    'Food & Cooking',
    'Technology & Gadgets',
    'Gaming',
    'Parenting & Family',
    'Education & Career',
    'Art & Design',
    'Music & Performance',
    'Finance & Investing',
    'Comedy & Entertainment',
    'Automotive',
    'Book Recommendations',
    'Sustainable Living',
    'Pet Influencers',
    'Spirituality & Mindfulness',
    'Minimalism',
    'DIY Home Renovation',
    'Business & Entrepreneurship',
    'Relationships & Dating',
    'Sports',
    'News & Commentary',
    'Movies & Television',
    'Home & Garden',
    'Events & Nightlife',
    'Faith & Religion',
    'Photography & Videography',
    'Wellness & Self-Care',
    'Luxury & Premium Lifestyle',
    'Local Culture & Community',
] as const;

export type NicheMainCategory = (typeof NICHE_MAIN_ORDER)[number];

/** Main categories sorted A–Z (for search / quick-pick UI). */
export const NICHE_MAIN_ALPHABETICAL: readonly NicheMainCategory[] = [...NICHE_MAIN_ORDER].sort((a, b) =>
    a.localeCompare(b),
);

export const NICHE_SUBS_BY_MAIN: Record<NicheMainCategory, readonly string[]> = {
    Fashion: [
        'Streetwear',
        'Luxury Fashion',
        'Thrift Fashion',
        'Vintage Fashion',
        'Accessories',
        'Footwear',
        'Sportswear Fashion',
        'Kids Fashion',
        'Fast Fashion',
        'Runway & Editorial',
        'Plus-Size Fashion',
        'Modest Fashion',
        'Menswear',
        'Womenswear',
    ],
    'Beauty & Skincare': [
        'Makeup Tutorials',
        'Skincare Routines',
        'K-Beauty',
        'Product Reviews',
        'DIY Skincare',
        'Beauty Hacks',
        'Clean Beauty',
        'Haircare',
        'Nails',
        "Men's Grooming",
        'Natural Hair',
        'Fragrance',
        'Nail Art',
        'Beauty Services',
    ],
    'Fitness & Health': [
        'Home Workouts',
        'Gym Life',
        'Yoga',
        'Nutrition Advice',
        'Mental Wellness',
        'Weight Loss',
        'HIIT Workouts',
        'Running',
        'Meditation',
        'Bodybuilding',
        'Pilates',
        'Sports Training',
        'Healthy Habits',
        'Recovery & Mobility',
    ],
    Lifestyle: [
        'Productivity Tips',
        'Morning Routines',
        'Budget Lifestyle',
        'Home Decor',
        'Organization Hacks',
        'Self-Care',
        'Dating Tips',
        'Daily Vlogs',
        'Apartment Living',
        'Adulting Tips',
        'College Life',
        'City Living',
        'Relationship Content',
        'Luxury Lifestyle',
        'Personal Development',
    ],
    'Travel & Adventure': [
        'Solo Travel',
        'Budget Travel',
        'Luxury Travel',
        'Travel Vlogs',
        'Digital Nomad',
        'Cultural Travel',
        'Adventure Travel',
        'Hidden Gems',
        'Family Travel',
        'Business Travel',
        'Road Trips',
        'Travel Tips',
        'Hotels & Resorts',
        'Outdoor Adventure',
    ],
    'Food & Cooking': [
        'Easy Recipes',
        'Vegan Cooking',
        'Baking',
        'Restaurant Reviews',
        'Meal Prep',
        'Food Challenges',
        'Street Food',
        'Fine Dining',
        'Food Vlogs',
        'Healthy Eating',
        'Cocktails & Beverages',
        'Cultural Cuisine',
    ],
    'Technology & Gadgets': [
        'Smartphone Reviews',
        'Tech Unboxings',
        'App Reviews',
        'Home Automation',
        'PC Builds',
        'Wearables',
        'AI Tools',
        'Artificial Intelligence',
        'Software Reviews',
        'Web Development',
        'Cybersecurity',
        'Smart Home',
        'Creator Technology',
    ],
    Gaming: [
        'Game Reviews',
        'Live Streams',
        'Esports',
        'Mobile Games',
        'Game Mods',
        'Cosplay',
        'Speedruns',
        'Mobile Gaming',
        'Console Gaming',
        'PC Gaming',
        'Gaming News',
        'Walkthroughs',
        'Gaming Hardware',
    ],
    'Parenting & Family': [
        'Toddler Life',
        'School Tips',
        'Family Vlogs',
        'Parenting Hacks',
        'Mom Life',
        'Dad Life',
        'Homeschooling',
        'New Parents',
        'Newborn Care',
        'Teen Parenting',
        'Family Travel',
        'Parenting Reviews',
        'Special Needs Parenting',
    ],
    'Education & Career': [
        'Study Tips',
        'Career Advice',
        'Resume Help',
        'Coding Tutorials',
        'Language Learning',
        'College Life',
        'Online Learning',
        'Scholarships',
        'Professional Development',
        'Remote Work',
        'Workplace Advice',
        'Entrepreneurship Education',
    ],
    'Art & Design': [
        'Digital Art',
        'Illustration',
        'Graphic Design',
        'Painting',
        'Sculpture',
        'UI/UX Design',
        'Calligraphy',
        'Crafts',
    ],
    'Music & Performance': [
        'Music Covers',
        'Original Music',
        'Dance',
        'Theater',
        'Instrument Tutorials',
        'DJ Sets',
        'Live Performances',
    ],
    'Finance & Investing': [
        'Personal Finance',
        'Stock Market',
        'Crypto',
        'Real Estate',
        'Side Hustles',
        'Budgeting Tips',
        'Retirement Planning',
    ],
    'Comedy & Entertainment': [
        'Skits',
        'Parodies',
        'Memes & Reactions',
        'Street Interviews',
        'Dark Humor',
        'Satire',
        'Stand-Up Comedy',
        'Pranks',
        'Impressions',
        'Storytelling',
        'Celebrity Commentary',
        'Pop Culture',
    ],
    Automotive: [
        'Car Reviews',
        'Modifications',
        'Motorsports',
        'Electric Vehicles',
        'Test Drives',
        'Auto DIY',
        'Motorcycles',
        'Car Maintenance',
        'Car Culture',
        'Luxury Cars',
        'Commercial Vehicles',
        'Auto Accessories',
    ],
    'Book Recommendations': ['Fiction', 'Non-Fiction', 'Self-Help', 'Book Reviews', 'Reading Vlogs', 'Audiobooks'],
    'Sustainable Living': [
        'Zero Waste',
        'Eco Products',
        'Green Home',
        'Sustainable Fashion',
        'Composting',
        'Renewable Energy',
    ],
    'Pet Influencers': ['Dog Content', 'Cat Content', 'Exotic Pets', 'Pet Care Tips', 'Pet Training', 'Pet Tricks'],
    'Spirituality & Mindfulness': [
        'Meditation',
        'Manifestation',
        'Astrology',
        'Tarot',
        'Mindfulness Practices',
        'Spiritual Healing',
    ],
    Minimalism: ['Minimalist Home', 'Capsule Wardrobe', 'Digital Minimalism', 'Simple Living', 'Decluttering'],
    'DIY Home Renovation': [
        'Room Makeovers',
        'Furniture Builds',
        'Painting & Wallpaper',
        'Plumbing & Electrical',
        'Landscaping',
        'Budget Renovations',
    ],
    'Business & Entrepreneurship': [
        'Startup Advice',
        'Small Business',
        'Entrepreneurship',
        'E-Commerce',
        'Marketing Tips',
        'Sales Advice',
        'Personal Branding',
        'Leadership',
    ],
    'Relationships & Dating': [
        'Dating Advice',
        'Relationship Advice',
        'Marriage',
        'Friendship',
        'Single Life',
        'Couples Content',
    ],
    Sports: [
        'Football',
        'Basketball',
        'Combat Sports',
        'Running',
        'Sports Commentary',
        'Sports Training',
        'Fan Content',
    ],
    'News & Commentary': ['Current Events', 'Pop Culture News', 'Social Commentary', 'Industry News', 'Explainers'],
    'Movies & Television': [
        'Movie Reviews',
        'TV Reviews',
        'Film Analysis',
        'Celebrity News',
        'Trailers & Reactions',
        'Anime',
    ],
    'Home & Garden': [
        'Home Decor',
        'Gardening',
        'Cleaning Tips',
        'Organization',
        'Interior Styling',
        'Home Maintenance',
    ],
    'Events & Nightlife': ['Event Hosting', 'Nightlife', 'Festivals', 'Concerts', 'Event Reviews', 'Behind the Scenes'],
    'Faith & Religion': [
        'Christian Content',
        'Islamic Content',
        'Faith Discussions',
        'Devotionals',
        'Religious Education',
        'Inspirational Content',
    ],
    'Photography & Videography': [
        'Photography Tutorials',
        'Videography Tutorials',
        'Photo Editing',
        'Video Editing',
        'Camera Reviews',
        'Cinematography',
    ],
    'Wellness & Self-Care': [
        'Self-Care Routines',
        'Stress Management',
        'Sleep Wellness',
        'Healthy Habits',
        'Emotional Wellness',
        'Spa & Relaxation',
    ],
    'Luxury & Premium Lifestyle': [
        'Luxury Products',
        'Luxury Cars',
        'Fine Dining',
        'Luxury Hotels',
        'Designer Collections',
        'High-End Experiences',
    ],
    'Local Culture & Community': [
        'Local Businesses',
        'Community Events',
        'Cultural Commentary',
        'City Guides',
        'Diaspora Content',
        'Traditions & Heritage',
    ],
};

/** Flat, deduplicated sub-category list sorted A–Z (for legacy flat validation). */
export const ALL_SUBCATEGORIES_SORTED: readonly string[] = (() => {
    const set = new Set<string>();
    for (const subs of Object.values(NICHE_SUBS_BY_MAIN)) {
        for (const sub of subs) set.add(sub);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
})();

export function isNicheMainCategory(value: string): value is NicheMainCategory {
    return (NICHE_MAIN_ORDER as readonly string[]).includes(value);
}

export function isValidSubcategory(sub: string): boolean {
    const normalized = sub.trim();
    return normalized.length > 0 && ALL_SUBCATEGORIES_SORTED.includes(normalized);
}

export function getSubcategoriesForMain(main: string): string[] {
    if (!isNicheMainCategory(main)) return [];
    return [...NICHE_SUBS_BY_MAIN[main]].sort((a, b) => a.localeCompare(b));
}

export function isValidSubForMain(main: string, sub: string): boolean {
    if (!isNicheMainCategory(main) || !sub.trim()) return false;
    return (NICHE_SUBS_BY_MAIN[main] as readonly string[]).includes(sub.trim());
}

/** First main category that lists this sub (subs may appear under multiple mains). */
export function findMainCategoryForSub(sub: string): NicheMainCategory | null {
    const normalized = sub.trim();
    if (!normalized) return null;
    for (const main of NICHE_MAIN_ORDER) {
        if ((NICHE_SUBS_BY_MAIN[main] as readonly string[]).includes(normalized)) {
            return main;
        }
    }
    return null;
}
